import { createContext } from 'react';

/**
 * Represents an individual skill from the Anthropic Skills spec
 * https://github.com/anthropics/skills
 */
export interface Skill {
  /** The name of the skill in hyphen-case (must match directory name) */
  name: string;
  /** Description of what the skill does and when to use it */
  description: string;
  /** Optional license applied to the skill */
  license?: string;
  /** Optional list of pre-approved tools */
  allowedTools?: string[];
  /** Optional metadata map for client-specific properties */
  metadata?: Record<string, string>;
  /** The full content of the SKILL.md file (instructions) */
  content: string;
}

/**
 * Represents a plugin that contains one or more skills
 */
export interface Plugin {
  /** Unique identifier for the plugin */
  id: string;
  /** Display name for the plugin */
  name: string;
  /** Source URL (GitHub, GitLab, etc.) or empty for built-in plugins */
  url: string;
  /** Whether the plugin is enabled */
  enabled: boolean;
  /** Skills contained in this plugin */
  skills: Skill[];
  /** When the plugin was last fetched */
  lastFetched?: number;
  /** Whether this is a default plugin (cannot be removed) */
  isDefault?: boolean;
  /** Whether this is a built-in plugin (hardcoded, cannot be refreshed) */
  isBuiltIn?: boolean;
}

export interface SkillsSettings {
  plugins: Plugin[];
}

export interface SkillsContextType {
  settings: SkillsSettings;
  addPlugin: (url: string) => Promise<void>;
  removePlugin: (id: string) => void;
  togglePlugin: (id: string) => void;
  refreshPlugin: (id: string) => Promise<void>;
  getEnabledSkills: () => Skill[];
  getSkillByName: (name: string) => Skill | undefined;
  isLoading: boolean;
}

export const SkillsContext = createContext<SkillsContextType | undefined>(undefined);
